import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircleIcon, WarningIcon, UserIcon } from "@phosphor-icons/react";

/**
 * Bulk driver sync result modal. Shown after POST /integrations/motive/sync-drivers.
 * Two panels: matched drivers (already linked or newly linked) and unmatched
 * ones with the reason (no_email / no_motive_match) so admins know which
 * Rep Fleet driver records to fix.
 */
export default function MotiveSyncReportModal({ open, onClose, report }) {
  if (!report) return null;
  const matched = report.matched_report || [];
  const unmatched = report.unmatched_report || [];
  const linkedNow = matched.filter((m) => m.status === "linked");
  const alreadyLinked = matched.filter((m) => m.status === "already_linked");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="motive-sync-report">
        <DialogHeader>
          <div className="section-eyebrow text-blue-600 mb-1">/// Motive sync report</div>
          <DialogTitle className="font-display font-black text-2xl tracking-tighter">
            Driver matching complete
          </DialogTitle>
        </DialogHeader>

        {/* KPI strip */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <Tile value={report.motive_drivers_scanned} label="Motive drivers scanned" tone="slate" />
          <Tile value={linkedNow.length} label="Linked now" tone="emerald" />
          <Tile value={alreadyLinked.length} label="Already linked" tone="slate" />
          <Tile value={unmatched.length} label="Unmatched" tone="amber" />
        </div>

        {/* Linked panel */}
        {linkedNow.length > 0 && (
          <Panel title={`Newly linked (${linkedNow.length})`} tone="emerald" icon={CheckCircleIcon}>
            {linkedNow.map((m) => (
              <Row key={m.driver_id} testId={`sync-linked-${m.driver_id}`}>
                <div className="font-bold text-sm text-slate-900">{m.name}</div>
                <div className="text-[11px] text-emerald-700 font-mono uppercase tracking-wide">
                  → {m.motive_name} · id {m.motive_driver_id}
                </div>
              </Row>
            ))}
          </Panel>
        )}

        {alreadyLinked.length > 0 && (
          <Panel title={`Already linked (${alreadyLinked.length})`} tone="slate" icon={CheckCircleIcon}>
            {alreadyLinked.map((m) => (
              <Row key={m.driver_id} testId={`sync-already-${m.driver_id}`}>
                <div className="text-sm text-slate-700">{m.name}</div>
                <div className="text-[11px] text-slate-500 font-mono">Motive id {m.motive_driver_id}</div>
              </Row>
            ))}
          </Panel>
        )}

        {/* Unmatched */}
        {unmatched.length > 0 ? (
          <Panel title={`Unmatched (${unmatched.length}) — please fix the email addresses`} tone="amber" icon={WarningIcon}>
            {unmatched.map((u) => (
              <Row key={u.driver_id} testId={`sync-unmatched-${u.driver_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-bold text-slate-900 flex items-center gap-1">
                      <UserIcon size={12} weight="bold" className="text-slate-500" />
                      {u.name}
                    </div>
                    <div className="text-[11px] text-slate-600 font-mono">
                      {u.email || "no email on file"}
                    </div>
                  </div>
                  <div className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${u.reason === "no_email" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                    {u.reason === "no_email" ? "add email" : "no motive match"}
                  </div>
                </div>
              </Row>
            ))}
          </Panel>
        ) : (
          matched.length > 0 && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm text-emerald-800">
              🎉 Every driver in Rep Fleet with an email is linked to Motive.
            </div>
          )
        )}

        {matched.length === 0 && unmatched.length === 0 && (
          <div className="mt-3 text-center text-sm text-slate-500 py-6">
            No Rep Fleet drivers found. Add drivers with emails, then re-run Sync.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Tile({ value, label, tone }) {
  const map = { emerald: "text-emerald-700", amber: "text-amber-700", slate: "text-slate-900" };
  return (
    <div className="bg-slate-50 rounded-sm p-3">
      <div className={`font-display font-black text-2xl tracking-tighter tabular-nums ${map[tone]}`}>{value}</div>
      <div className="font-mono text-[10px] uppercase text-slate-500 font-bold tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function Panel({ title, tone, icon: Icon, children }) {
  const bg = tone === "emerald" ? "bg-emerald-50 border-emerald-200" : tone === "amber" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200";
  const text = tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-700";
  return (
    <div className={`mt-3 border rounded-sm ${bg}`}>
      <div className="px-3 py-2 border-b border-current border-opacity-20">
        <div className={`text-xs font-bold uppercase font-mono tracking-wide flex items-center gap-1.5 ${text}`}>
          <Icon size={14} weight="bold" />
          {title}
        </div>
      </div>
      <div className="p-2 max-h-64 overflow-y-auto space-y-1">
        {children}
      </div>
    </div>
  );
}

function Row({ children, testId }) {
  return (
    <div className="bg-white rounded-sm px-3 py-2 border border-slate-200" data-testid={testId}>
      {children}
    </div>
  );
}
